<template>
  <div
    v-if="showModal"
    class="fill-summary-modal"
    @click.self="closeModal"
  >
    <div class="modal-content">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <h2 class="modal-title">{{ headerTitle }}</h2>
          <div class="close-btn" @click="closeModal">
            <img
              src="/icons/close.svg"
              alt="close"
              class="close-icon day-mode"
            />
            <img
              src="/icons/close-night.svg"
              alt="close"
              class="close-icon night-mode"
            />
          </div>
        </div>
      </div>

      <div class="body">
        <p class="summary-note">{{ summaryNote }}</p>

        <!-- Bet meta -->
        <div class="meta">
          <div class="meta-row">
            <span class="meta-label">Game</span>
            <span class="meta-value">{{ data.gameInfo }}</span>
          </div>
          <div class="meta-row">
            <span class="meta-label">Selection</span>
            <span class="meta-value">{{ data.sideString }}</span>
          </div>
        </div>

        <!-- Wanted vs Got comparison -->
        <div class="compare">
          <div class="compare-row compare-head">
            <span class="cell label"></span>
            <span class="cell">Wanted</span>
            <span class="cell got">Got</span>
          </div>

          <div class="compare-row" :class="{ changed: oddsChanged }">
            <span class="cell label">Avg odds</span>
            <span class="cell">{{ data.wanted.odds }}</span>
            <span class="cell got">{{ data.got.odds }}</span>
          </div>

          <div class="compare-row" :class="{ changed: partial }">
            <span class="cell label">Risk</span>
            <span class="cell">{{ money(data.wanted.risk) }}</span>
            <span class="cell got">{{ money(data.got.risk) }}</span>
          </div>

          <div class="compare-row" :class="{ changed: partial || oddsChanged }">
            <span class="cell label">To win</span>
            <span class="cell">{{ money(data.wanted.win) }}</span>
            <span class="cell got">{{ money(data.got.win) }}</span>
          </div>
        </div>

        <p v-if="appliedSlippageNote" class="summary-note">
          {{ appliedSlippageNote }}
        </p>
      </div>

      <!-- Action Button -->
      <div class="modal-actions">
        <button class="continue-btn" @click="closeModal">Got it</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const showModal = ref(false);
const data = ref({
  gameInfo: '',
  sideString: '',
  wanted: { odds: '', risk: 0, win: 0 },
  got: { odds: '', risk: 0, win: 0 },
  reasons: { partial: false, oddsChanged: false },
});

const partial = computed(() => !!data.value?.reasons?.partial);
const oddsChanged = computed(() => !!data.value?.reasons?.oddsChanged);

const headerTitle = computed(() => {
  if (partial.value) return 'Partial Fill';
  if (oddsChanged.value) return 'Filled at Different Odds';
  return 'Bet Taken';
});

// Tolerance disclosure. The caller passes a number ONLY when a slip
// actually governed the fill (100%-frontend design — no server clamp, so
// the old `true` "generic wording" tri-state is gone; spec-review 25). Not
// gated on oddsChanged: a governed-but-small slip below the 0.5-point
// display threshold still discloses.
const appliedSlippageNote = computed(() => {
  const pct = Number(data.value?.appliedSlippagePct);
  if (!Number.isFinite(pct) || pct <= 0) return '';
  return `Booked within your ${pct}% odds slippage tolerance.`;
});

const summaryNote = computed(() => {
  const notes = [];
  if (partial.value) {
    notes.push(
      `Only ${money(data.value.got.risk)} of your ${money(
        data.value.wanted.risk,
      )} order was matched — the rest of the line was no longer available.`,
    );
  }
  if (oddsChanged.value) {
    notes.push(
      `You were filled at ${data.value.got.odds} instead of the projected ${data.value.wanted.odds}.`,
    );
  }
  return notes.join(' ');
});

function money(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '$0.00';
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function show(fillData) {
  data.value = {
    gameInfo: fillData?.gameInfo ?? '',
    sideString: fillData?.sideString ?? '',
    wanted: fillData?.wanted ?? { odds: '', risk: 0, win: 0 },
    got: fillData?.got ?? { odds: '', risk: 0, win: 0 },
    reasons: fillData?.reasons ?? { partial: false, oddsChanged: false },
    appliedSlippagePct: fillData?.appliedSlippagePct,
  };
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

defineExpose({
  show,
  closeModal,
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

.fill-summary-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: main.$black-202;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050; // above the exchange offers dock, under the base modal wrapper
  padding: 1rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    padding: 0.5rem;
  }
}

.modal-content {
  background: main.$white_01;
  border-radius: 12px;
  box-shadow: 0 4px 20px main.$black-201;
  width: 100%;
  max-width: 450px;
  overflow: hidden;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    max-width: 100%;
    border-radius: 8px;
  }
}

.modal-header {
  background: main.$color-primary_02;
  padding: 1rem 1.5rem;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-title {
    color: main.$white;
    font-size: 1.25rem;
    font-weight: main.$font-weight-bold;
    margin: 0;
    font-family: 'Poppins Bold', Arial, sans-serif;
  }

  .close-btn {
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    transition: background-color 0.2s ease;

    &:hover {
      background: main.$black-103;
    }

    .close-icon {
      width: 20px;
      height: 20px;
    }
  }
}

.body {
  padding: 1.5rem;
  background: main.$white;
}

.summary-note {
  margin: 0 0 1.25rem;
  color: main.$black-203;
  font-size: 0.875rem;
  line-height: 1.4;
  font-family: 'Poppins Medium', Arial, sans-serif;
}

.meta {
  margin-bottom: 1.25rem;

  .meta-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 0.4rem 0;

    .meta-label {
      color: main.$black-203;
      font-weight: main.$font-weight-bold;
      font-size: 0.8125rem;
      font-family: 'Poppins Medium', Arial, sans-serif;
    }

    .meta-value {
      color: main.$black;
      font-weight: main.$font-weight-bold;
      font-size: 0.8125rem;
      text-align: right;
      font-family: 'Poppins Bold', Arial, sans-serif;
    }
  }
}

.compare {
  border: 1px solid main.$grey-402;
  border-radius: 8px;
  overflow: hidden;

  .compare-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid main.$grey-402;

    &:last-child {
      border-bottom: none;
    }

    .cell {
      font-size: 0.875rem;
      text-align: right;
      color: main.$black;
      font-weight: main.$font-weight-bold;
      font-family: 'Poppins Bold', Arial, sans-serif;

      &.label {
        text-align: left;
        color: main.$black-203;
        font-family: 'Poppins Medium', Arial, sans-serif;
      }

      &.got {
        color: main.$color-primary;
      }
    }
  }

  .compare-head {
    background: main.$white-200;

    .cell {
      font-size: 0.75rem;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: main.$black-203;
      font-family: 'Poppins Medium', Arial, sans-serif;

      &.got {
        color: main.$color-primary;
      }
    }
  }

  .compare-row.changed {
    .cell.got {
      color: main.$red-300;
    }
  }
}

.modal-actions {
  padding: 1.5rem;
  background: main.$white_01;
  display: flex;
  justify-content: center;

  .continue-btn {
    background: main.$color-primary;
    color: main.$white;
    border: none;
    border-radius: 8px;
    padding: 0.875rem 2rem;
    font-size: 1rem;
    font-weight: main.$font-weight-bold;
    font-family: 'Poppins Bold', Arial, sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 120px;

    &:hover {
      background: main.$color-primary_04;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px main.$black-103;
    }

    &:active {
      transform: translateY(0);
    }

    @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
      padding: 1rem 1.5rem;
      font-size: 0.875rem;
    }
  }
}

// Night mode
[theme='night'] {
  .modal-content {
    background: main.$white_01;
  }

  .body {
    background: main.$white_01;
  }

  .summary-note {
    color: main.$black-203_01;
  }

  .meta .meta-row {
    .meta-label {
      color: main.$black-203_01;
    }
    .meta-value {
      color: main.$black;
    }
  }

  .compare {
    border-color: main.$grey-400;

    .compare-row {
      border-bottom-color: main.$grey-400;

      .cell {
        color: main.$black;

        &.label {
          color: main.$black-203_01;
        }
      }
    }

    .compare-head {
      background: main.$white_07;

      .cell {
        color: main.$black-203_01;
      }
    }
  }

  .modal-actions {
    background: main.$white_01;

    .continue-btn {
      background: main.$color-primary_04;
      color: main.$black;

      &:hover {
        background: main.$color-primary_07;
        box-shadow: 0 4px 12px main.$black-104;
      }
    }
  }

  .close-btn {
    .day-mode {
      display: none;
    }
    .night-mode {
      display: block;
    }
  }
}

[theme='day'] {
  .close-btn {
    .day-mode {
      display: block;
    }
    .night-mode {
      display: none;
    }
  }
}
</style>
